<script setup lang="ts">
/**
 * マルチプレイゲームのメイン画面
 */
import { onMounted, onUnmounted, watch, ref, computed } from 'vue';
import { useGameStore } from '@/stores/game';
import { useRoomStore } from '@/stores/room'; // roomStoreをインポート
// import { useAuthStore } from '@/stores/auth'; // authStoreを追加
import ImageDisplay from '@/components/ImageDisplay.vue';
import AnswerChoices from '@/components/AnswerChoices.vue';
import AnswerStats from '@/components/AnswerStats.vue';
import Leaderboard from '@/components/Leaderboard.vue';
import ShareButtons from '@/components/ShareButtons.vue';
import FinalScoreboard from '@/components/FinalScoreboard.vue';
import { useRoute, useRouter } from 'vue-router'; // useRouterを追加
import { useI18n } from 'vue-i18n';

const gameStore = useGameStore();
const roomStore = useRoomStore(); // roomStoreを使用
// const authStore = useAuthStore(); // authStoreを使用
const { t } = useI18n();
const route = useRoute();
const roomId = Array.isArray(route.params.roomId) ? route.params.roomId[0] : route.params.roomId; // ルートパラメータからルームIDを取得

// コンポーネントがマウントされたら
onMounted(async () => {


  // roomStoreにgameStoreを登録して、WebSocketのイベントを受け取れるようにする
  roomStore.registerGameStore(gameStore);

  // ホストかどうかをgameStoreに設定

  gameStore.setIsHost(roomStore.isHost);

  // RoomDetailViewから遷移する場合、WebSocketは既に接続され、最初の問題が送られているはず。

  // 参加者チェック: 部屋に参加していない場合は詳細画面に戻す (直接URLアクセスや予期せぬ遷移への対策)
  if (!roomStore.isPlayerInRoom) {
    console.warn('MultiplayerGameView.vue: User is NOT in the room player list. Redirecting to Room Detail.');
    router.replace({ name: 'RoomDetail', params: { roomId: roomId } });
    return;
  }

  // roomStoreのWebSocketをgameStoreに共有する
  if (roomStore.websocket) {
    gameStore.setWebSocket(roomStore.websocket);
  } else {
    console.warn('MultiplayerGameView.vue: roomStore.websocket is null. Connection might be lost.');
  }

  // roomStoreのWebSocketをgameStoreに共有する


  // ゲーム開始時にタイマーを起動するために、currentQuestionを監視するウォッチャーを設定
  // これはgame.jsのsetQuestionDataでquestions.valueが更新された際にトリガーされる
});

// コンポーネントがアンマウントされたら
onUnmounted(() => {
  console.log('MultiplayerGameView.vue: onUnmounted - Stopping timer and leaving room channel.');
  gameStore.stopTimer(); // ストアのタイマーを停止

  // 画面から離れる際に部屋から退出する (WebSocket接続は維持)
  roomStore.leaveRoomChannel();
});

const router = useRouter(); // useRouteは既にあるがuseRouterが必要
const localNextGameType = ref<string>('zoom_out');
const showGameSelectModal = ref(false);

// currentQuestionが更新されたらタイマーをリセットして再開
// currentQuestionが更新されたらタイマーをリセットして再開 -> ストア側で制御するため削除
// watch(() => gameStore.currentQuestion, (newQuestion) => {
//
//   if (newQuestion && newQuestion.game_details) {
//     // 現在のゲームタイプを次回のデフォルトに設定
//     localNextGameType.value = newQuestion.game_details.game_type || 'zoom_out';
//   }
// }, { immediate: true });

const handlePlayAgain = async () => {
  if (!roomStore.isHost) return;
  showGameSelectModal.value = true;
};

const handleSelectGameMode = async (mode: string) => {
  if (!roomId) return;
  showGameSelectModal.value = false;

  try {
    await roomStore.updateRoomMode(roomId, mode);
    console.log(`Room updated to ${mode}`);
    await gameStore.startGameViaWebSocket(roomId, mode);
  } catch (e) {
    console.error('Failed to update room or restart game:', e);
    alert('ゲームの開始に失敗しました。');
  }
};

// 部屋一覧に戻る (全員)
const handleBackToRoomList = () => {
  // 部屋一覧へ遷移 (onUnmountedでleaveRoomChannelが呼ばれる)
  router.push({ name: 'RoomList' });
};

// currentImageUrlの更新を監視
// watch(() => gameStore.currentImageUrl, (newImage) => {
//
// });

// answerResultの更新を監視(デバッグ用)
// answerResultの更新を監視(デバッグ用) - 削除
// watch(() => gameStore.answerResult, (newResult, oldResult) => { ... }, { deep: true });

const handleChoiceSelected = (choiceId: number) => {
  gameStore.selectAnswer(choiceId);
  gameStore.stopTimer(); // 回答したらストアのタイマーを停止
};

// シェア機能用
const shareUrl = computed(() => window.location.origin);
const hashTags = ['ズームアウトゲーム', 'マルチプレイ', 'クイズ'];

const shareText = computed(() => {
  if (!gameStore.finalScores) return t('share.play_text');

  const myScore = gameStore.finalScores.find(s => s.is_me) || gameStore.finalScores[0];

  if (myScore) {
    let score = myScore.score ?? myScore.total_score;
    // 平均点を計算
    if (gameStore.totalQuestions > 0) {
      score = Math.floor(score / gameStore.totalQuestions * 10) / 10;
    }
    return t('share.result_text', { score });
  }

  return t('share.play_text');
});
</script>

<template>
  <div class="game-container">
    <h1>{{ t('multiplayer_game.title') }}</h1>
    <h2 v-if="!gameStore.currentQuestion">{{ t('game.loading_question') }}</h2>


    <!-- ゲーム終了メッセージ -->
    <div v-if="gameStore.isGameFinished" class="game-end-message">
      <!-- 最終スコアボードの表示 -->
      <ShareButtons :share-url="shareUrl" :share-text="shareText" :hash-tags="hashTags" />

      <!-- ホスト用ゲームモード変更 (再戦設定) -->


      <FinalScoreboard v-if="gameStore.finalScores && gameStore.finalScores.length > 0"
        :final-scores="gameStore.finalScores" :total-questions="gameStore.totalQuestions" :is-multiplayer="true"
        :is-host="roomStore.isHost" @retry="handlePlayAgain" @back="handleBackToRoomList"
        @play-another="handlePlayAgain">
      </FinalScoreboard>

      <!-- ゲームモード選択モーダル -->
      <div v-if="showGameSelectModal" class="modal-overlay" @click.self="showGameSelectModal = false">
        <div class="modal-content">
          <button class="modal-close-icon" @click="showGameSelectModal = false">×</button>
          <h3>{{ t('room_create.game_mode_label') }}</h3>
          <div class="mode-buttons-grid">
            <button class="game-select-btn mix-btn" @click="handleSelectGameMode('mix')">
              <span class="btn-icon">🎨</span>
              <span class="btn-text">{{ t('room_create.game_mode_mix') }}</span>
            </button>
            <button class="game-select-btn zoom-out-btn" @click="handleSelectGameMode('zoom_out')">
              <span class="btn-icon">🔍</span>
              <span class="btn-text">{{ t('room_create.game_mode_zoom_out') }}</span>
            </button>
            <button class="game-select-btn resolution-btn" @click="handleSelectGameMode('resolution')">
              <span class="btn-icon">🖼️</span>
              <span class="btn-text">{{ t('room_create.game_mode_resolution') }}</span>
            </button>
            <button class="game-select-btn noise-btn" @click="handleSelectGameMode('noise')">
              <span class="btn-icon">🌫️</span>
              <span class="btn-text">{{ t('room_create.game_mode_noise') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ホスト向け再プレイボタンはFinalScoreboard内で制御が難しい(Actionボタンがemitするだけなので)
           ただし、FinalScoreboardはボタンを表示する。
           ホストだけが「もう一度遊ぶ(retry/play-another)」を実行できるように制御が必要だが、
           FinalScoreboardは汎用的なので、全員にボタンが見えてしまう。
           マルチプレイの場合、ホスト以外が押しても何もしないか、あるいは
           FinalScoreboardを使わずにここだけ独自実装するか。
           
           ユーザーの要望「シングルプレイの実装参考に修正して」に従い、FinalScoreboardを使う。
           ただし、マルチプレイの仕様(ホストのみ再開可能)と整合性を取る必要がある。
           
           FinalScoreboardのボタン(Retry/PlayAnother)を押したときに、
           handlePlayAgain内で isHost チェックを行えば、機能的には問題ない。
           ボタンが表示されてしまうことへの違和感はあるかもしれないが、
           「リトライ」を押しても「ホストのみ実行可能です」とアラート出すか、
           あるいはFinalScoreboardに `isHost` propを渡してボタン出しわけするか。
           
           現状のFinalScoreboardには isHost propはない。
           
           一旦、FinalScoreboardをそのまま使い、Host以外が押しても無視される(handlePlayAgainでのガード)形にする。
      -->
    </div>

    <!-- 問題表示エリア -->
    <template v-else-if="gameStore.currentQuestion && gameStore.currentQuestion.game_details">
      <p>Room ID: {{ roomId }}</p>

      <div class="header-info">
        <div class="time-display info-badge">
          <span class="label">{{ t('game.time_remaining') }}</span>
          <!-- カウントダウン中は前の問題の残り時間が見えないように --- を表示 -->
          <span class="value">{{ gameStore.isCountdownActive ? '---' : gameStore.remainingTime.toFixed(3) + 's'
            }}</span>
        </div>
        <div class="question-counter-top info-badge">
          <span class="label">{{ t('game.question_count') }}</span>
          <span class="value">{{ gameStore.currentDisplayQuestionNumber }} / {{ gameStore.totalQuestions }}</span>
        </div>
        <div class="answered-count info-badge">
          <span class="label">{{ t('game.answer_count') }}</span>
          <span class="value">{{ gameStore.answeredCount }} / {{ gameStore.totalActivePlayers }}</span>
        </div>
      </div>

      <ImageDisplay :key="gameStore.currentQuestion.id" :image-url="gameStore.currentImageUrl" />
      <!-- 中間ランキング表示 (正解発表の後) -->
      <Leaderboard v-if="gameStore.leaderboard" :leaderboard="gameStore.leaderboard"
        :question-count="gameStore.currentDisplayQuestionNumber" />

      <!-- 回答分布の表示 -->
      <AnswerStats v-if="gameStore.answerStats" :stats="gameStore.answerStats"
        :choices="gameStore.currentQuestion.choices" />

      <!-- 回答結果の表示 -->
      <div v-if="gameStore.answerResult && gameStore.answerResult.type === 'my_group_result'" class="answer-result"
        :class="gameStore.answerResult.isCorrect ? 'correct' : 'incorrect'">
        {{ gameStore.answerResult.username }}: {{ gameStore.answerResult.message }} ({{
          gameStore.answerResult.scoreEarned }}{{ t('scoreboard.points_suffix') }})
      </div>

      <!-- 他のプレイヤーの回答待ちメッセージ -->
      <div
        v-else-if="gameStore.isAnswered && !gameStore.answerResult && !gameStore.answerStats && !gameStore.leaderboard"
        class="waiting-message">
        {{ t('room_status.waiting') }}...
      </div>

      <!-- 選択肢の表示 (まだ回答してない場合) -->
      <AnswerChoices v-if="!gameStore.isAnswered && !gameStore.answerStats && !gameStore.leaderboard"
        :choices="gameStore.currentQuestion.choices" @select-choice="handleChoiceSelected" />

      <!-- TODO: 他のプレイヤーの回答状況やスコア表示エリア -->
      <!-- <div class="multiplayer-info"> ... </div> -->
    </template>

    <div v-else>
      <p>{{ t('common.loading') }}...</p>
    </div>
  </div>
</template>

<style scoped>
.header-info {
  display: flex;
  justify-content: center;
  gap: 0.2rem;
  width: 100%;
  max-width: 320px;
  /* 350px -> 320px */
  margin-bottom: 5px;
  /* 3px -> 5px */
  flex-wrap: nowrap;
}

.info-badge {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.8rem 1.2rem;
  min-width: 0;
  flex: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
  white-space: nowrap;
}

.info-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.08);
}

.info-badge .label {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-badge .value {
  font-size: 1.4rem;
  font-weight: 800;
  color: #495057;
  font-family: 'Roboto Mono', monospace;
}

@media (max-width: 600px) {
  .header-info {
    gap: 0.5rem;
  }

  .info-badge {
    padding: 0.5rem 0.5rem;
    flex-direction: column;
    align-items: center;
  }

  .info-badge .value {
    font-size: 1.1rem;
  }

  .info-badge .label {
    font-size: 0.7rem;
    margin-bottom: 0;
  }
}

.time-display .value {
  color: #333;
}


.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: auto;
  padding: 0.2rem;
  box-sizing: border-box;
  font-family: 'Arial', sans-serif;
  text-align: center;
}

h1 {
  margin-bottom: 1rem;
  color: #333;
}

h2 {
  font-size: 2.5rem;
  color: #007bff;
  /* 青系の色 */
  margin-bottom: 1rem;
}

.question-counter {
  margin-top: 1rem;
  font-size: 1.2rem;
  color: #666;
}

.answer-result {
  margin-top: 5px;
  /* 3px -> 5px */
  margin-bottom: 5px;
  /* 3px -> 5px */
  font-size: 2rem;
  font-weight: bold;
  padding: 1rem 2rem;
  border-radius: 10px;
  min-height: 80px;
  /* 高さを確保してレイアウトがガタつくのを防ぐ */
  display: flex;
  align-items: center;
  justify-content: center;
}

.correct {
  color: #fff;
  background-color: #28a745;
  border: 3px solid #1e7e34;
}

.incorrect {
  color: #fff;
  background-color: #dc3545;
  border: 3px solid #b21f2d;
}

.game-end-message {
  margin-top: 5px;
  /* 3rem -> 5px */
  font-size: 2.5rem;
  font-weight: bold;
  color: #007bff;
  /* ゲーム終了メッセージを目立たせる色 */
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.time-display {
  font-size: 1.8rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1rem;
  /* 画像との間に余白 */
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background-color: #e0e0e0;
  /* 背景色を追加して見やすく */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.multiplayer-info {
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid #ddd;
  border-radius: 10px;
  background-color: #f9f9f9;
  width: 80%;
  max-width: 600px;
}

.multiplayer-info h3 {
  color: #555;
  margin-bottom: 1rem;
}

.waiting-message {
  margin-top: 5px;
  /* 3px -> 5px */
  margin-bottom: 5px;
  /* 3px -> 5px */
  font-size: 1.5rem;
  font-weight: bold;
  color: #007bff;
  padding: 1rem 2rem;
  border-radius: 10px;
  background-color: #e7f3ff;
  border: 2px solid #007bff;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.final-scoreboard {
  margin-top: 2rem;
  padding: 2rem;
  background-color: #fff;
  border-radius: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 100%;
}

.final-scoreboard h3 {
  color: #333;
  margin-bottom: 1.5rem;
  font-size: 2rem;
  text-align: center;
}

.score-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.score-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  border-radius: 10px;
  background-color: #f5f5f5;
  border: 2px solid #ddd;
  font-size: 1.3rem;
  transition: transform 0.2s;
}

.score-item:hover {
  transform: translateY(-2px);
}

.score-item .rank {
  font-weight: bold;
  min-width: 60px;
  color: #666;
}

.score-item .username {
  flex: 1;
  font-weight: bold;
  color: #333;
  margin: 0 1rem;
}

.score-item .score {
  font-weight: bold;
  color: #007bff;
  min-width: 120px;
  text-align: right;
}

.score-item.first-place {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-color: #daa520;
  box-shadow: 0 4px 8px rgba(255, 215, 0, 0.3);
}

.score-item.first-place .rank,
.score-item.first-place .username {
  color: #8b6914;
}

.score-item.first-place .score {
  color: #8b6914;
}

.score-item.second-place {
  background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
  border-color: #a0a0a0;
  box-shadow: 0 4px 8px rgba(192, 192, 192, 0.3);
}

.score-item.second-place .rank,
.score-item.second-place .username {
  color: #555;
}

.score-item.second-place .score {
  color: #555;
}

.score-item.third-place {
  background: linear-gradient(135deg, #cd7f32 0%, #e5a76b 100%);
  border-color: #b8722c;
  box-shadow: 0 4px 8px rgba(205, 127, 50, 0.3);
}

.score-item.third-place .rank,
.score-item.third-place .username {
  color: #5c3d1f;
}

.score-item.third-place .score {
  color: #5c3d1f;
}

.action-buttons {
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 0.8rem 1.5rem;
  font-size: 1.2rem;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  color: white;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn:active {
  transform: translateY(0);
}

.btn-primary {
  background-color: #007bff;
  box-shadow: 0 4px 6px rgba(0, 123, 255, 0.2);
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  box-shadow: 0 4px 6px rgba(108, 117, 125, 0.2);
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.current-user-score {
  border: 3px solid #007bff !important;
  /* Blue border to highlight */
  background-color: #f0f8ff !important;
  /* AliceBlue background */
}

.you-badge {
  font-size: 0.8em;
  color: #28a745;
  margin-left: 5px;
  font-weight: bold;
}
</style>

<style scoped>
.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: auto;
  padding: 0.2rem;
  box-sizing: border-box;
  font-family: 'Arial', sans-serif;
  text-align: center;
}

h1 {
  margin-bottom: 1rem;
  color: #333;
}

h2 {
  font-size: 2.5rem;
  color: #007bff;
  /* 青系の色 */
  margin-bottom: 1rem;
}

.question-counter {
  margin-top: 1rem;
  font-size: 1.2rem;
  color: #666;
}

.answer-result {
  margin-top: 5px;
  /* 3px -> 5px */
  margin-bottom: 5px;
  /* 3px -> 5px */
  font-size: 2rem;
  font-weight: bold;
  padding: 1rem 2rem;
  border-radius: 10px;
  min-height: 80px;
  /* 高さを確保してレイアウトがガタつくのを防ぐ */
  display: flex;
  align-items: center;
  justify-content: center;
}

.correct {
  color: #fff;
  background-color: #28a745;
  border: 3px solid #1e7e34;
}

.incorrect {
  color: #fff;
  background-color: #dc3545;
  border: 3px solid #b21f2d;
}

.game-end-message {
  margin-top: 5px;
  /* 3rem -> 5px */
  font-size: 2.5rem;
  font-weight: bold;
  color: #007bff;
  /* ゲーム終了メッセージを目立たせる色 */
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.time-display {
  font-size: 1.8rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1rem;
  /* 画像との間に余白 */
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background-color: #e0e0e0;
  /* 背景色を追加して見やすく */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.multiplayer-info {
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid #ddd;
  border-radius: 10px;
  background-color: #f9f9f9;
  width: 80%;
  max-width: 600px;
}

.multiplayer-info h3 {
  color: #555;
  margin-bottom: 1rem;
}

.waiting-message {
  margin-top: 5px;
  /* 3px -> 5px */
  margin-bottom: 5px;
  /* 3px -> 5px */
  font-size: 1.5rem;
  font-weight: bold;
  color: #007bff;
  padding: 1rem 2rem;
  border-radius: 10px;
  background-color: #e7f3ff;
  border: 2px solid #007bff;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.final-scoreboard {
  margin-top: 2rem;
  padding: 2rem;
  background-color: #fff;
  border-radius: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 100%;
}

.final-scoreboard h3 {
  color: #333;
  margin-bottom: 1.5rem;
  font-size: 2rem;
  text-align: center;
}

.score-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.score-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  border-radius: 10px;
  background-color: #f5f5f5;
  border: 2px solid #ddd;
  font-size: 1.3rem;
  transition: transform 0.2s;
}

.score-item:hover {
  transform: translateY(-2px);
}

.score-item .rank {
  font-weight: bold;
  min-width: 60px;
  color: #666;
}

.score-item .username {
  flex: 1;
  font-weight: bold;
  color: #333;
  margin: 0 1rem;
}

.score-item .score {
  font-weight: bold;
  color: #007bff;
  min-width: 120px;
  text-align: right;
}

.score-item.first-place {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-color: #daa520;
  box-shadow: 0 4px 8px rgba(255, 215, 0, 0.3);
}

.score-item.first-place .rank,
.score-item.first-place .username {
  color: #8b6914;
}

.score-item.first-place .score {
  color: #8b6914;
}

.score-item.second-place {
  background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
  border-color: #a0a0a0;
  box-shadow: 0 4px 8px rgba(192, 192, 192, 0.3);
}

.score-item.second-place .rank,
.score-item.second-place .username {
  color: #555;
}

.score-item.second-place .score {
  color: #555;
}

.score-item.third-place {
  background: linear-gradient(135deg, #cd7f32 0%, #e5a76b 100%);
  border-color: #b8722c;
  box-shadow: 0 4px 8px rgba(205, 127, 50, 0.3);
}

.score-item.third-place .rank,
.score-item.third-place .username {
  color: #5c3d1f;
}

.score-item.third-place .score {
  color: #5c3d1f;
}

.action-buttons {
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 0.8rem 1.5rem;
  font-size: 1.2rem;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  color: white;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn:active {
  transform: translateY(0);
}

.btn-primary {
  background-color: #007bff;
  box-shadow: 0 4px 6px rgba(0, 123, 255, 0.2);
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  box-shadow: 0 4px 6px rgba(108, 117, 125, 0.2);
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.current-user-score {
  border: 3px solid #007bff !important;
  /* Blue border to highlight */
  background-color: #f0f8ff !important;
  /* AliceBlue background */
}

.you-badge {
  font-size: 0.8em;
  color: #28a745;
  margin-left: 5px;
  font-weight: bold;
}

/* Modal CSS - Refined */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  /* Move up */
  padding-top: 15vh;
  /* Upper position */
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

.modal-content {
  background: #fff;
  padding: 2rem;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  position: relative;
  /* Stylish Border */
  border: 4px solid #fff;
  outline: 4px solid #4a90e2;
  /* Blue outline */
  animation: slideDown 0.3s ease;
}

.modal-close-icon {
  position: absolute;
  top: 10px;
  right: 15px;
  background: #f0f0f0;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 1.5rem;
  color: #555;
  cursor: pointer;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.modal-close-icon:hover {
  background: #ddd;
  color: #333;
  transform: scale(1.1);
}

.mode-buttons-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 2rem;
}

.game-select-btn {
  padding: 1.5rem 1rem;
  font-size: 1.1rem;
  color: #fff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  gap: 0.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.game-select-btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
}

.btn-icon {
  font-size: 2rem;
}

.zoom-out-btn {
  background: linear-gradient(135deg, #42b983 0%, #369f6e 100%);
}

.resolution-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}

.noise-btn {
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}

.mix-btn {
  background: linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%);
}

@media (max-width: 500px) {
  .mode-buttons-grid {
    grid-template-columns: 1fr;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
